import React, { useMemo, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RoutesPaths } from "../Router/constant";

const desktopNavItems = [
  { key: "home", to: RoutesPaths.HOME },
  { key: "about", to: RoutesPaths.ABOUT },
  { key: "ourPosition", to: RoutesPaths.OURPOSITION },
  { key: "esg", to: RoutesPaths.ESG },
    { key: "contact", to: RoutesPaths.CONTACT },  
];

const solutionItems = [
  { key: "privateSector", to: RoutesPaths.PRIVATE_SECTOR },
  { key: "publicSector", to: RoutesPaths.PUBLIC_SECTOR },
];

const mobileNavItemsBeforeSolutions = desktopNavItems.slice(0, 3);
const mobileNavItemsAfterSolutions = desktopNavItems.slice(3);

const languageOptions = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "pt", label: "PT" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const isSolutionsActive = useMemo(() => {
    return solutionItems.some((item) => location.pathname === item.to);
  }, [location.pathname]);

  const currentLanguage = String(i18n.resolvedLanguage || i18n.language || "en")
    .toLowerCase()
    .startsWith("fr")
    ? "fr"
    : String(i18n.resolvedLanguage || i18n.language || "en")
        .toLowerCase()
        .startsWith("pt")
      ? "pt"
      : "en";

  const getDesktopNavClass = ({ isActive }) =>
    `nav-link f-s-16 f-w-500 c-p${isActive ? " active-nav" : ""}`;

  const getMobileNavClass = ({ isActive }) =>
    `nav-link${isActive ? " active-mobile-nav" : ""}`;

  const mobileSolutionsButtonClass = `nav-link header-dropdown__toggle header-dropdown__toggle--mobile${
    isSolutionsActive || solutionsOpen ? " active-mobile-nav" : ""
  }`;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSolutionsOpen(false);
  }, [location.pathname]);

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
  };

  return (
    <div
      className={`header-section ${scrolled ? "headerScrolled" : ""} ${
        open ? "menu-expanded" : ""
      } ${location.pathname === RoutesPaths.ESG ? "header-section--esg" : ""} ${location.pathname === RoutesPaths.CONTACT ? "header-section--contact" : ""} ${location.pathname === RoutesPaths.OURPOSITION ? "header-section--ourposition" : ""}`}
    >
      <div className="main-container">
        <header className="header-main">
          <div className="header-main__row d-flex align-items-center justify-content-between">
            <div className="header-logo">
              <NavLink to={RoutesPaths.HOME}>
                <img src="/images/pro-vest-fsp-logo.png" alt="PRO-VEST FSP" width="2069" height="424" />
              </NavLink>
            </div>

            <ul className="nav header-nav align-items-center justify-content-center d-none d-lg-flex">
              {desktopNavItems.slice(0, 3).map((item) => (
                <li className="nav-item" key={item.to}>
                  <NavLink
                    className={getDesktopNavClass}
                    to={item.to}
                    onClick={() => setSolutionsOpen(false)}
                  >
                    {t(`header.nav.${item.key}`)}
                  </NavLink>
                </li>
              ))}

              <li
                className="nav-item header-dropdown"
                onMouseEnter={() => setSolutionsOpen(true)}
                onMouseLeave={() => setSolutionsOpen(false)}
              >
                <button
                  type="button"
                  className={`nav-link f-s-16 f-w-500 c-p header-dropdown__toggle${
                    isSolutionsActive ? " active-nav" : ""
                  }`}
                  onClick={() => setSolutionsOpen((prev) => !prev)}
                  aria-expanded={solutionsOpen}
                  aria-haspopup="true"
                >
                  {t("header.nav.solutions")}
                </button>

                <div
                  className={`header-dropdown__menu${solutionsOpen ? " is-open" : ""}`}
                >
                  {solutionItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `header-dropdown__item${isActive ? " is-active" : ""}`
                      }
                      onClick={() => setSolutionsOpen(false)}
                    >
                      {t(`header.nav.${item.key}`)}
                    </NavLink>
                  ))}
                </div>
              </li>

              {desktopNavItems.slice(3).map((item) => (
                <li className="nav-item" key={item.to}>
                  <NavLink
                    className={getDesktopNavClass}
                    to={item.to}
                    onClick={() => setSolutionsOpen(false)}
                  >
                    {t(`header.nav.${item.key}`)}
                  </NavLink>
                </li>
              ))}
            </ul>

            <div className="header-main__actions d-flex align-items-center gap-2">
              <div
                className="language-switcher d-none d-lg-inline-flex"
                role="group"
                aria-label={t("common.actions.changeLanguage")}
              >
                {languageOptions.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    className={`language-switcher__button${
                      currentLanguage === option.code ? " is-active" : ""
                    }`}
                    onClick={() => handleLanguageChange(option.code)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="menu-toggle d-lg-none"
                onClick={() => setOpen(!open)}
                aria-label={t("common.actions.openMenu")}
              >
                &#9776;
              </button>
            </div>
          </div>

          <div className={`mobile-menu d-lg-none ${open ? "open" : ""}`}>
            {mobileNavItemsBeforeSolutions.map((item) => (
              <NavLink
                className={getMobileNavClass}
                key={item.to}
                to={item.to}
                onClick={() => setSolutionsOpen(false)}
              >
                {item.key === "contact"
                  ? t("common.cta.contact")
                  : t(`header.nav.${item.key}`)}
              </NavLink>
            ))}

            <div className="mobile-dropdown">
              <button
                type="button"
                className={mobileSolutionsButtonClass}
                onClick={() => setSolutionsOpen((prev) => !prev)}
                aria-expanded={solutionsOpen}
                aria-haspopup="true"
              >
                {t("header.nav.solutions")}
              </button>

              <div
                className={`mobile-dropdown__menu${solutionsOpen ? " is-open" : ""}`}
              >
                {solutionItems.map((item) => (
                  <NavLink
                    className={({ isActive }) =>
                      `mobile-dropdown__item${isActive ? " active-mobile-nav" : ""}`
                    }
                    key={item.to}
                    to={item.to}
                    onClick={() => setSolutionsOpen(false)}
                  >
                    {t(`header.nav.${item.key}`)}
                  </NavLink>
                ))}
              </div>
            </div>

            {mobileNavItemsAfterSolutions.map((item) => (
              <NavLink
                className={getMobileNavClass}
                key={item.to}
                to={item.to}
                onClick={() => setSolutionsOpen(false)}
              >
                {item.key === "contact"
                  ? t("common.cta.contact")
                  : t(`header.nav.${item.key}`)}
              </NavLink>
            ))}

            <div
              className="language-switcher language-switcher--mobile"
              role="group"
              aria-label={t("common.actions.changeLanguage")}
            >
              {languageOptions.map((option) => (
                <button
                  key={option.code}
                  type="button"
                  className={`language-switcher__button${
                    currentLanguage === option.code ? " is-active" : ""
                  }`}
                  onClick={() => handleLanguageChange(option.code)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </header>
      </div>
    </div>
  );
}
